/* eslint-disable react-hooks/exhaustive-deps */
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import HttpLeadRepository from "../../api/request/Lead/Lead.service";
import HttpDocumentsRepository from "../../api/request/Documents/Documents.service";
import { GetlifeContext } from "../../context/GetLifeContext.context";
import { I18nContext } from "../../context/i18n.context";
import { useLocation, useNavigate } from "react-router";
import {
  CoverageT,
  ProductI,
  NewProductI,
} from "../../api/request/Lead/Interfaces/ProductInterface.response";
import { GetLifeTrackerContext } from "../../context/GetLifeTracker.context";
import { trackSubmissionError, trackValidationError } from "../../helpers/Tracking";
import { upsellCapitalOf, UpsellVisit } from "../../helpers/Tracking/upsell";
import {
  DayAvailable,
  GlobalDomainQuestions,
  DaysAvailable,
  formatPhoneWithCallCode,
} from "../../utils/InternationlChanges";
import { hashIdentity } from "../../utils/hashIdentity";
import { useDevice, GlobalIcon } from "get-life-storybook-ts";
import { datePickerI } from "get-life-storybook-ts/lib/molecules/CustomPolicy/CustomPolicy";
import { CapitalsResponse, DataI } from "../../api/request/Lead/Interfaces/DataInterface.response";
import { StatusContext } from "../../context/StatusContext";
import { DownloadableFilesT } from "get-life-storybook-ts/lib/components/DownloadableFiles/DownloadableFiles";
import { DOMAIN, PARSE_DOCS } from "../../constants/Global";
import { isInViewport } from "../../utils/Utils";
import { getCookie } from "../../hooks/getCookie";
import { useTraitState } from "../../hooks/useTraitState";
import { usePendingCapitalAdjustment } from "../../hooks/usePendingCapitalAdjustment";
import {
  QuestionTreeErrorKindT,
  questionTreeErrorKind,
} from "../Questions/QuestionTreeError";
import { TraitBoundary } from "../../api/request/Lead/Interfaces/TraitTypes";
import numberFormat, { annualizePremium, CONVERSION_CURRENCY, numberFormatPayment } from "../../utils/Utils";

const STORAGE = "https://storage.googleapis.com/getlife-bucket-1/public";

const IPID = `${STORAGE}/pdf/IPID-GTD.pdf`;

export interface UpsellControllerI {
  product: ProductI;
  checkOut: DataI;
  setUpsellData: Dispatch<SetStateAction<DataI | null>>;
  setUpsellProduct: Dispatch<SetStateAction<ProductI | null>>;
}

const UpsellController = () => {
  const {
    handleCallMe,
    leadId,
    token,
    domain,
    backFlag,
    isABtesting,
    sendEventsNewLead,
    setSendEventsNewLead,
    utm_campaign,
    utm_source,
    utm_content,
    utm_medium,
    utm_term,
    utm_test,
    gclid,
    msclkid,
    fbclid,
    gtf_campaign,
    getEntryPage,
    getReferrerUrl,
    getABTestVersion,
    nodeMaxCapitalModal,
    setToast,
    setToastIsOpen,
  } = useContext(GetlifeContext);
  const { handleTrackerQuestion, identifyFS } = useContext(
    GetLifeTrackerContext
  );
  const {
    state: { translate },
  } = useContext(I18nContext);
  const { getStatus } = useContext(StatusContext);

  const location = useLocation();
  const paymentLocation = location?.pathname.split("/")[1];

  const nodeConditionsModal: any = useRef(null);

  const handleConditions = () =>
    nodeConditionsModal.current?.handleShowPortal();

  const [product, setProduct] = useState<NewProductI>();
  // 503/502 de validate-medical (árbol de preguntas caído o versión no disponible):
  // el upsell no se carga y se enseña el aviso, en vez de seguir como si nada.
  const [questionTreeError, setQuestionTreeError] =
    useState<QuestionTreeErrorKindT | null>(null);
  const [isRetryingQuestionTree, setIsRetryingQuestionTree] = useState(false);

  const [capital, setCapital] = useState(0);
  const [ipa, setIpa] = useState("");
  const [newIpa, setNewIpa] = useState<CoverageT | boolean>(false);

  const [upGradedOpen, setUpGradedOpen] = useState(false);
  const [changeDone, setChangeDone] = useState(false);
  const [capitalAdded, setCapitalAdded] = useState(false);
  const [capitalAddedAmount, setCapitalAddedAmount] = useState(0);

  const [isStickyVisible, setIsStickyVisible] = useState<Boolean>(false);

  const [priceChange, setPriceChange] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [downloadableFiles, setDownloadableFiles] =
    useState<DownloadableFilesT>();
  const [documentUrls, setDocumentUrls] = useState<Record<string, string>>({});

  const [afterPromoText, setAfterPromoText] = useState<string>("");
  const [beforePromoText, setBeforePromoText] = useState<string>("");

  const [initialTraitCapitals, setInitialTraitCapitals] = useState<Record<string, number>>({ DEATH: 0 });
  const [traitBoundariesData, setTraitBoundariesData] = useState<TraitBoundary[]>([]);
  const [globalAccMax, setGlobalAccMax] = useState(0);

  const lastUpgradedRef = useRef(false);
  // Telemetría del upsell (contrato 3.2) de esta entrada: lo que ve al entrar (para
  // saber si al continuar lo mejora), sus cambios de capital y el tiempo hasta decidir.
  const [upsellVisit] = useState(() => new UpsellVisit());

  const { isMobile } = useDevice();

  const refIPA = useRef<any>();

  const linkRef = useRef<HTMLAnchorElement | null>(null);

  const leadRepository = new HttpLeadRepository(leadId, token);

  const convertCapitals = (capitals: CapitalsResponse) => {
    const initialCapitals: Record<string, number> = {};
    const boundaries: TraitBoundary[] = [];

    for (const [trait, entry] of Object.entries(capitals)) {
      if (entry.active) {
        initialCapitals[trait] = entry.capital;
      }
      boundaries.push({
        trait,
        available: entry.available,
        min: entry.min,
        max: entry.max,
      });
    }

    return { initialCapitals, traitBoundaries: boundaries };
  };

  const refreshTraitState = (upsellData: NewProductI) => {
    if (upsellData.capitals) {
      const { initialCapitals, traitBoundaries } = convertCapitals(upsellData.capitals);
      setInitialTraitCapitals(initialCapitals);
      setTraitBoundariesData(traitBoundaries);
      setGlobalAccMax(upsellData.globalAccumulatedMax ?? 0);
      setCapital(initialCapitals.DEATH ?? upsellData.capital);
    } else if (upsellData.traitBoundaries && upsellData.traitBoundaries.length > 0) {
      setTraitBoundariesData(upsellData.traitBoundaries as TraitBoundary[]);
      setGlobalAccMax(upsellData.globalAccumulatedMax ?? 0);
      if (upsellData.capitalsByTrait) {
        setInitialTraitCapitals(upsellData.capitalsByTrait);
      } else {
        setInitialTraitCapitals({ DEATH: upsellData.capital });
      }
    }
  };

  const {
    pendingAdjustment,
    captureAdjustmentError,
    confirmAdjustment,
    cancelAdjustment,
  } = usePendingCapitalAdjustment();

  const refreshUpsellData = useCallback(async () => {
    const repo = new HttpLeadRepository(leadId, token);
    const upsellData: NewProductI = await repo.getUpsellData();
    setProduct(upsellData);
    refreshTraitState(upsellData);
    setAfterPromoText(upsellData?.afterPromoText);
    setBeforePromoText(upsellData?.beforePromoText);

    // Surface upgrade/downgrade modals on every recalculation (toggles and
    // capital slider changes), not only on the initial load.
    if (upsellData.upgraded && !lastUpgradedRef.current) {
      setUpGradedOpen(true);
    }
    lastUpgradedRef.current = !!upsellData.upgraded;

    if (upsellData.downgraded) {
      setCapital(upsellData.downgradedCapitalMaximum);
      nodeMaxCapitalModal.current?.handleShowPortal();
    }
    return upsellData;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId, token]);

  const runTraitRecalculation = useCallback(
    async (capitals: Record<string, number>) => {
      const repo = new HttpLeadRepository(leadId, token);
      await repo.calculateMultiTraitPrices(leadId, capitals);
      return refreshUpsellData();
    },
    [leadId, token, refreshUpsellData]
  );

  const onTraitRecalculate = useCallback(async (newCapitals: Record<string, number>) => {
    setPriceChange(true);
    // Capital libre del upsell (3.2): el de fallecimiento, si este recálculo lo cambia.
    const requestedCapital = upsellVisit.capitalRequest(newCapitals);
    try {
      const recalculated = await runTraitRecalculation(newCapitals);
      upsellVisit.capitalRecalculated(requestedCapital, recalculated);
    } catch (error) {
      // Capital fuera de límites: el servidor propone un ajuste (error de validación).
      if (captureAdjustmentError(error, newCapitals)) trackValidationError("capital", "api");
      else trackSubmissionError(error);
      upsellVisit.capitalRecalculated(requestedCapital, null);
    } finally {
      setPriceChange(false);
    }
  }, [runTraitRecalculation, captureAdjustmentError, upsellVisit]);

  const confirmPendingAdjustment = useCallback(async () => {
    await confirmAdjustment(async (adjustedCapitals) => {
      setPriceChange(true);
      const requestedCapital = upsellVisit.capitalRequest(adjustedCapitals);
      try {
        const recalculated = await runTraitRecalculation(adjustedCapitals);
        upsellVisit.capitalRecalculated(requestedCapital, recalculated);
      } catch {
        /* silenced: user stays on previous state */
        upsellVisit.capitalRecalculated(requestedCapital, null);
      } finally {
        setPriceChange(false);
      }
    });
  }, [confirmAdjustment, runTraitRecalculation, upsellVisit]);

  const cancelPendingAdjustment = useCallback(async () => {
    cancelAdjustment();
    setPriceChange(true);
    try {
      await refreshUpsellData();
    } catch {
      /* silenced: keep current state if refresh fails */
    } finally {
      setPriceChange(false);
    }
  }, [cancelAdjustment, refreshUpsellData]);

  const pendingAdjustmentModal = pendingAdjustment
    ? {
        isOpen: true,
        title: translate("edit.projects.capital.confirm.title"),
        message: translate("edit.projects.capital.adjusted")
          .replace(
            "{trait}",
            translate(`trait.${pendingAdjustment.trait}`) ||
              pendingAdjustment.trait
          )
          .replace(
            "{capital}",
            numberFormat(pendingAdjustment.deathCapital, "capital") as string
          ),
        acceptLabel: translate("edit.projects.capital.confirm.accept"),
        cancelLabel: translate("edit.projects.capital.confirm.cancel"),
        onConfirm: confirmPendingAdjustment,
        onCancel: cancelPendingAdjustment,
      }
    : {
        isOpen: false,
        title: "",
        message: "",
        acceptLabel: "",
        cancelLabel: "",
        onConfirm: () => {},
        onCancel: cancelAdjustment,
      };

  const traitState = useTraitState({
    initialCapitals: initialTraitCapitals,
    traitBoundaries: traitBoundariesData,
    globalAccumulatedMax: globalAccMax,
    onRecalculate: onTraitRecalculate,
  });

  const today = new Date();
  const aMonthMore = new Date().setMonth(today.getMonth() + 1);

  const handleUpgradedClose = () => {
    setUpGradedOpen(false);
    lastUpgradedRef.current = true;
  };

  const handleIpaChange = (newIpa: any) => {
    setNewIpa(newIpa);
    setIpa(newIpa);
  };

  const handleProduct = async (
    newCapital: number,
    newIpa: any
  ): Promise<any> => {
    setCapital(newCapital);
    setIpa(newIpa);
    setPriceChange(true);
    try {
      const upsellData: NewProductI = await leadRepository.getUpsellData();

      setProduct(upsellData);
      if (upsellData.downgraded) {
        setCapital(upsellData.downgradedCapitalMaximum);
        nodeMaxCapitalModal.current?.handleShowPortal();
        return handleProduct(upsellData.downgradedCapitalMaximum, newIpa);
      }
      if (upsellData) {
        setAfterPromoText(upsellData?.afterPromoText);
        setBeforePromoText(upsellData?.beforePromoText);
      }
    } catch (error) {
      setToast({
        onClose: () => setToastIsOpen(false),
        title: translate("upsell.CallMeBack.warningTitle"),
        message: translate("upsell.CallMeBack.warningText"),
        state: "error",
        autoCloseIn: 3000,
      });
      setToastIsOpen(true);
    } finally {
      setPriceChange(false);
    }
  };

  const navigate = useNavigate();

  const selectPlan = async () => {
    if (isSubmitting || priceChange || traitState.loading) return;
    setIsSubmitting(true);
    try {
      upsellVisit.decide(product);
      if (DOMAIN === "es" || DOMAIN === "wl_es") {
        await leadRepository.updatePeriod("yearly");
      }
      navigate(`/${paymentLocation}/3`);
    } catch (error) {
      trackSubmissionError(error);
      setToast({
        onClose: () => setToastIsOpen(false),
        title: translate("upsell.CallMeBack.warningTitle"),
        message: translate("upsell.CallMeBack.warningText"),
        state: "error",
        autoCloseIn: 3000,
      });
      setToastIsOpen(true);
      setIsSubmitting(false);
    }
  };

  const correctPrice = (property: keyof NewProductI): any => {
    return product ? product[property] : null;
  };

  const documentsRepository = new HttpDocumentsRepository(leadId, token);

  const getAvailablesDocuments = async (lead: string) => {
    documentsRepository.getAvailablesDocuments(lead).then((resp) => {
      let allDocs = [] as unknown as DownloadableFilesT;
      resp.forEach((element: string) => {
        allDocs.push({
          label: PARSE_DOCS(translate)[0][element],
          type: element,
        });
      });

      setDownloadableFiles(allDocs!);

      // Resolve every signed URL in the background so that a click on a
      // doc link can open the tab synchronously with the real URL — no
      // "about:blank" intermediate tab, no popup-blocker flicker.
      resp.forEach(async (element: string) => {
        try {
          const res = await documentsRepository.downloadDocument(element);
          if (res?.documentUrl) {
            setDocumentUrls((prev) => ({
              ...prev,
              [element]: res.documentUrl,
            }));
          }
        } catch {
          /* fall back to the async open flow when the user clicks */
        }
      });
    });
  };

  const downloadDocument = (type: string) => {
    const prefetched = documentUrls[type];
    if (prefetched) {
      // Prefetched → open the new tab synchronously on the click gesture.
      window.open(prefetched, "_blank", "noopener,noreferrer");
      return;
    }

    // Fallback for the rare case where the prefetch is still in flight
    // or failed: open a blank tab synchronously (otherwise the popup
    // blocker would swallow it) and retarget it once the URL resolves.
    // We must NOT pass "noopener" in the features string here because
    // doing so makes window.open return null and leaves us no handle.
    const pending = window.open("about:blank", "_blank");
    if (!pending) return;
    documentsRepository
      .downloadDocument(type)
      .then((res) => {
        if (res?.documentUrl) {
          pending.location.href = res.documentUrl;
          try {
            pending.opener = null;
          } catch {
            /* some browsers don't allow writing opener — ignore */
          }
        } else {
          pending.close();
        }
      })
      .catch((err) => {
        pending.close();
        console.error("Error downloading document", err);
      });
  };

  const openLink = () => {
    linkRef.current?.click();
  };

  const externalLink = {
    showLink: domain === "fr",
    linkClick: openLink,
    linkText: translate("upsell.IPID.text"),
    linkRef,
    linkUrl: IPID,
  };

  const changeCapitalTooltip = (price: string) => {
    setChangeDone(!changeDone);
    handleProduct(Number(price), ipa);
    setCapitalAdded(false);
  };

  const addCapital = async () => {
    !capitalAdded && setCapitalAddedAmount(product!.extraCapitalAmount || 0);
    let newCapital = capitalAdded
      ? product!.capital - capitalAddedAmount
      : product!.capital + product!.extraCapitalAmount;
    setCapitalAdded(!capitalAdded);
    await handleProduct(newCapital, ipa);
  };

  const correctPricePromotion = (property: keyof NewProductI) => {
    if (!!product!.promotion && product!.regularPrice !== product!.priceToPay) {
      return product!.priceToPay;
    }

    return correctPrice(property);
  };

  const renderPriceValidation = () => {
    const date = new Date(aMonthMore);
    return `${translate("newUpsell.banner.priceValidate")} ${date.getDate()}/${
      date.getMonth() + 1
    }/${date.getFullYear()}`;
  };

  const datePickerProps = (): datePickerI => {
    const today = new Date();
    const startDate = new Date(
      new Date(today).setDate(today.getDate() + DayAvailable[domain])
    );
    let finalDate = new Date(
      new Date().setDate(today.getDate() + DaysAvailable[domain])
    );
    const object = {
      initialDate: product!.effectDate,
      locale: GlobalDomainQuestions[domain],
      title: translate("upsell.DatePicker.title"),
      subtext1: translate("upsell.DatePicker.subtitle1"),
      subtext2: translate("upsell.DatePicker.subtitle2"),
      subtext3: translate("upsell.DatePicker.subtitle3"),
      textToday: translate("upsell.DatePicker.textToday"),
      textTomorrow: translate("upsell.DatePicker.textTomorrow"),
      textAfterTomorrow: translate("upsell.DatePicker.textAfterTomorrow"),
      notShowIpa: true,
      onValueChange: changeInitDate,
      overlayButtonLabel: translate("productQuestions.overlay.button"),
      maxDate: finalDate.toString(),
      minDate: startDate,
    };

    return object;
  };

  const buildScopeItems = (
    type: "yes" | "no",
    count: number,
    iconName: string,
    iconColor: string
  ) =>
    Array.from({ length: count }, (_, i) => {
      const idx = i + 1;
      const title = translate(
        `newUpsell.v3.coverageScope.${type}.${idx}.title`
      );
      const desc = translate(
        `newUpsell.v3.coverageScope.${type}.${idx}.desc`
      );
      return {
        iconElement: (
          <div>
            <GlobalIcon
              iconName={iconName as any}
              size="XS"
              color={iconColor}
            />
          </div>
        ),
        text: `${title}. ${desc}`,
      };
    });

  const listSectionData = [
    {
      iconElement: (
        <div>
          <GlobalIcon iconName="ShieldCheckIcon" size="XS" color="#018565" />
        </div>
      ),
      title: translate("newUpsell.listSection.cover"),
      list: buildScopeItems("yes", 5, "FilledCheckIcon", "#018565"),
    },
    {
      iconElement: (
        <div>
          <GlobalIcon iconName="MoodConfuzedIcon" size="XS" color="#686868" />
        </div>
      ),
      title: translate("newUpsell.listSection.notCover"),
      // IT (D.lgs. 209/2025): show 5 exclusions (carenza, preesistenze,
      // suicidio Y1, attività ad alto rischio, altre non dichiarate) instead
      // of the default 3. Non-IT markets keep 3 (items 4/5 have no translation).
      list: buildScopeItems("no", DOMAIN === "it" ? 5 : 3, "XIcon", "#EA5F5F"),
    },
  ];

  const changeInitDate = (date: any) => {
    let dateFormat = `${date.getFullYear()}-${String(
      date.getMonth() + 1
    ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    leadRepository.updateEffectDate(dateFormat).then((res) => {
      getProductData();
    });
  };

  const preparePromoText = (data: NewProductI) => {
    if (data.promotion && (data.promotion.promotionalCode === "10_sconto" || data.promotion.promotionalCode === "facile" || data.promotion.promotionalCode === "segugio10")) {
      setAfterPromoText(
        `Al posto di ${numberFormatPayment(data.regularPrice)} € `
      );
      setBeforePromoText(``);
    } else {
      setAfterPromoText(data.afterPromoText ?? "");
      setBeforePromoText(data.beforePromoText ?? "");
    }
  }

  const getProductData = async () => {
    await leadRepository.getUpsellData().then(async (response: NewProductI) => {
      setProduct(response);

      setCapital(response.capital);
      setIpa(response.coverage);
      setUpGradedOpen(response.upgraded);
      lastUpgradedRef.current = !!response.upgraded;

      refreshTraitState(response);

      preparePromoText(response);

      // Una vez por visita (cambiar la fecha de efecto vuelve a cargar los datos).
      // Sin tarjetas de capital: options 0.
      upsellVisit.view({
        initialCapital: upsellCapitalOf(response),
        hasPromotion: Boolean(response.promotion),
        baseline: response,
      });

        const event = "finalQuoteGenerated";
        const href = window?.location?.href ?? location.pathname;
        const identity = await hashIdentity({
          email: response.email,
          phone: formatPhoneWithCallCode(response.phone),
        });
        const dataInfo = {
          ...identity,
          postalCode: response.zipcode,
          gender: response.gender,
          platform: "App",
          page: href,
          leadId: leadId,
          calculatedCapital: response.capitals?.DEATH?.capital ?? response.capital,
          calculatedPremium: annualizePremium(response.regularPrice, response.period),
          currency: CONVERSION_CURRENCY,
          coverage: response.coverage === "premium" ? "FIPA" : "F",
          utm_source: getCookie("utm_source"),
          utm_campaign: getCookie("utm_campaign"),
          utm_medium: getCookie("utm_medium"),
          utm_content: getCookie("utm_content"),
          utm_term: getCookie("utm_term"),
          utm_test: getCookie("utm_test"),
          gclid: getCookie("gclid"),
          gbraid: getCookie("gbraid"),
          wbraid: getCookie("wbraid"),
          msclkid: getCookie("msclkid"),
          fbclid: getCookie("fbclid"),
          entryPage: getEntryPage(),
          referrerUrl: getReferrerUrl(),
          ABTestVersion: getABTestVersion()
        }
        handleTrackerQuestion({
          type: "FS",
          event,
          data: {
            birthDate: new Date(response.birthdate),
            ...dataInfo
          }
        });

        handleTrackerQuestion({
          type: "GA",
          data: {
            event,
            birthDate: response.birthdate,
            ...dataInfo
          },
        });
    });
  };

  // Validar estado de preguntas médicas antes de cargar el upsell
  const validateMedicalAndLoad = async () => {
    try {
      const validation = await leadRepository.validateMedicalQuestions();
      if (validation.status === "questions_pending") {
        navigate("/medicalQuestions");
        return;
      }
    } catch (error) {
      console.error("Error validating medical questions:", error);
      const kind = questionTreeErrorKind(error);
      if (null !== kind) {
        trackSubmissionError(error);
        setQuestionTreeError(kind);
        return;
      }
    }

    setQuestionTreeError(null);
    await getProductData();
  };

  // Reintento a mano del aviso (solo 503): repite la validación y la carga.
  const retryQuestionTree = async () => {
    setIsRetryingQuestionTree(true);
    try {
      await validateMedicalAndLoad();
    } finally {
      setIsRetryingQuestionTree(false);
    }
  };

  useEffect(() => {
    void validateMedicalAndLoad();
  }, []);

  useEffect(() => {
    if (process.env.REACT_APP_ENVIROMENT !== "DEV") {
      if (!backFlag) {
        getStatus();
      }
    }
  }, []);

  useEffect(() => {
    if (newIpa) {
      setNewIpa(false);
      handleProduct(capital, ipa);
    }
  }, [ipa]);

  useEffect(() => {
    if (token) getAvailablesDocuments(leadId);
  }, [product, token]);

  useEffect(() => {
    const toggleStickyBar = () => {
      const quoteBanner = document.getElementById("upsell_page_resume_cart");
      const quoteCTA = document.getElementById("upsell_page_upsell_cta");
      const isBannerInViewport: Boolean = isInViewport(quoteBanner);
      const isCTAInViewport: Boolean = isInViewport(quoteCTA);
      if ((isBannerInViewport || isCTAInViewport) === isStickyVisible) {
        setIsStickyVisible(!(isBannerInViewport || isCTAInViewport));
      }
    };
    document.addEventListener("scroll", toggleStickyBar);
    return () => window.removeEventListener("scroll", toggleStickyBar);
  }, [isStickyVisible]);

  const couponInfo =
    product && product.promotion
      ? {
          conditions: product?.promotion?.conditions,
          exceptions: product?.promotion?.exceptions,
          code: product?.promotion?.promotionalCode,
        }
      : undefined;

  return {
    addCapital,
    capital,
    capitalAdded,
    capitalAddedAmount,
    changeCapitalTooltip,
    changeDone,
    correctPrice,
    correctPricePromotion,
    datePickerProps,
    domain,
    externalLink,
    handleCallMe,
    handleIpaChange,
    handleUpgradedClose,
    ipa,
    isMobile,
    listSectionData,
    priceChange,
    refIPA,
    renderPriceValidation,
    selectPlan,
    setChangeDone,
    translate,
    upGradedOpen,
    product,
    downloadableFiles,
    isABtesting,
    downloadDocument,
    afterPromoText,
    beforePromoText,
    isStickyVisible,
    nodeConditionsModal,
    handleConditions,
    couponInfo,
    traitState,
    pendingAdjustmentModal,
    gtf_campaign,
    isSubmitting,
    questionTreeError,
    retryQuestionTree,
    isRetryingQuestionTree,
  };
};

export default UpsellController;
